import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { SearchUserRequest, searchUser, getUserWallets } from "src/network/graphql/userService";
// import { UserListRequest } from "src/network/services/UsersServices";
import { Wallet } from "../wallets/walletSlice";
import { Card } from "../cards/cardSlice";
import { CardSearchRequest, cardSearch } from "src/network/graphql/cardService";
import { TransactionRequest, getAllTransaction } from "src/network/graphql/transactionService";
import { Transaction } from "../transactions/transactionSlice";
import { getGroupDetail, getGroupList, SearchGroupListRequest } from "src/network/graphql/groupService";
// import { PARTICIPANT } from "src/components/constant/constant";
// import { walletListRequest } from "src/network/services/walletService";

export type UserGroup = {
  id: string;
  fullName: string;
  secondaryText: string;
  type: "USER" | "GROUP";
};

const initialUsers: UserGroup[] = [];
const initialWallets: Wallet[] = [];
const initialCards: Card[] = [];
const initialTransaction: Transaction[] = [];

const budgetSlice = createSlice({
  name: "budget",
  initialState: {
    userLoading: false,
    walletLoading: false,
    cardLoading: false,
    activityLoading: false,
    error: null,
    users: {
      list: initialUsers,
      total: 0
    },
    wallets: {
      list: initialWallets,
      total: 0
    },
    cards: {
      list: initialCards,
      total: 0
    },
    transactions: {
      list: initialTransaction,
      total: 0
    }
  },
  reducers: {
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = action.payload?.list ?? [];
      state.wallets.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = [];
      state.wallets.total = 0;
      state.error = action.payload;
    },
    resetwalletListAction: (state, action) => {
      state.wallets.list = action.payload.list;
      state.wallets.total = action.payload?.total;
    },
    fetchCardsStart: (state) => {
      state.cardLoading = true;
    },
    fetchCardsSuccess: (state, action) => {
      state.cardLoading = false;
      state.cards.list = action.payload;
      state.error = null;
    },
    fetchCardsFail: (state, action) => {
      state.cardLoading = false;
      state.error = action.payload;
    },
    fetchActivityStart: (state) => {
      state.activityLoading = true;
    },
    fetchActivitySuccess: (state, action) => {
      state.activityLoading = false;
      state.transactions = action.payload;
    },
    fetchActivityFail: (state, action) => {
      state.activityLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  fetchCardsStart,
  fetchCardsSuccess,
  fetchCardsFail,
  fetchActivityStart,
  fetchActivitySuccess,
  fetchActivityFail
} = budgetSlice.actions;

export const { resetwalletListAction } = budgetSlice.actions;

export const fetchUserListAction = (userData: SearchUserRequest, groupData: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const [userResponse, groupResponse] = await Promise.all([searchUser(userData), getGroupList(groupData)]);

      const userStructuredResponse = userResponse.data.search_user.user_listing.map((user: any) => ({
        id: user.user_id,
        fullName: user.attributes.first_name + " " + user.attributes.last_name,
        secondaryText: user.email,
        type: "USER"
      }));

      const groupStructuredResponse = groupResponse.data.search_group.items.map((group: any) => ({
        id: group.group_id,
        fullName: group.name,
        secondaryText: group.group_type,
        type: "GROUP"
      }));

      dispatch(
        fetchUsersSuccess({
          list: [...userStructuredResponse, ...groupStructuredResponse],
          total: userResponse.data.search_user.total_size + groupResponse.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchWalletListAction = (userId: string, type: "USER" | "GROUP") => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      let wallets;

      switch (type) {
        case "USER": {
          const userResponse = await getUserWallets(userId);

          wallets = userResponse.data.get_user_wallets;
          break;
        }
        case "GROUP": {
          const groupResponse = await getGroupDetail(userId);

          wallets = groupResponse.data.get_group.wallets;
          break;
        }
      }

      const structuredResponse = wallets.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        name: wallet.name
      }));

      dispatch(
        fetchWalletsSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export const fetchCardListAction = (data: CardSearchRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchCardsStart());
    try {
      const response = await cardSearch(data);
      const structuredResponse = response.data.search_card.card_listing?.map(
        (card: any) =>
          ({
            id: card.card_id,
            bsb: card.bsb,
            cardStatus: card.card_status,
            accessType: card.card_type,
            cardNumber: card.ppan,
            cardLimit: card.card_limit,
            user: {
              id: card.user_id,
              fullName: card.holder_name
            }
          }) as Card
      );

      dispatch(
        fetchCardsSuccess({
          list: structuredResponse,
          total: response.data.search_card.total_size
        })
      );
    } catch (error) {
      dispatch(fetchCardsFail(error));
    }
  };
};

export const fetchActivityAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchActivityStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((data: any) => ({
        id: data.activity_id,
        displayId: data.display_id,
        name: data.activity_name,
        endDate: data.end_timestamp,
        startDate: data.start_timestamp,
        ownerId: data.owner_id,
        activityStatus: data.activity_status,
        data: JSON.parse(data.data)
      }));

      dispatch(
        fetchActivitySuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchActivityFail(err));
    }
  };
};

export default budgetSlice.reducer;
